import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import type { ResolverConfigDto } from "../../../../types/resolver";
import { listResolverConfigsPaginated } from "../../../../utils/api";

const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_SEARCH_PAGE_SIZE = 10;

export interface ResolverConfigToolbarProps {
    rubricVersionId?: number;
    loadedConfigId: number | null;
    configName: string;
    isSaving: boolean;
    loadError: string | null;
    saveError: string | null;
    saveSuccess: boolean;
    setActiveError: string | null;
    onLoad: (config: ResolverConfigDto) => void;
    onNew: () => void;
    onSave: () => void;
    onSetActive: () => void;
    onDeleteClick: () => void;
    onDismissLoadError: () => void;
    onDismissSaveError: () => void;
    onDismissSaveSuccess: () => void;
    onDismissSetActiveError: () => void;
}

export function ResolverConfigToolbar({
    rubricVersionId,
    loadedConfigId,
    configName,
    isSaving,
    loadError,
    saveError,
    saveSuccess,
    setActiveError,
    onLoad,
    onNew,
    onSave,
    onSetActive,
    onDeleteClick,
    onDismissLoadError,
    onDismissSaveError,
    onDismissSaveSuccess,
    onDismissSetActiveError,
}: ResolverConfigToolbarProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<ResolverConfigDto[]>([]);
    const [selectedConfig, setSelectedConfig] = useState<ResolverConfigDto | null>(null);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchConfigList = useCallback(
        (search: string) => {
            setIsLoadingList(true);
            listResolverConfigsPaginated({
                ...(search.trim() ? { search: search.trim() } : {}),
                rubricVersionId,
                page: 0,
                size: DEFAULT_SEARCH_PAGE_SIZE,
                sortBy: "name",
                sortDirection: "asc",
            })
                .then((page) => {
                    setSearchResults(page.content ?? []);
                })
                .catch(() => {
                    setSearchResults([]);
                })
                .finally(() => {
                    setIsLoadingList(false);
                });
        },
        [rubricVersionId]
    );

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        const query = searchQuery.trim();
        if (!query) {
            setSearchResults([]);
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            searchDebounceRef.current = null;
            fetchConfigList(query);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [searchQuery, fetchConfigList]);

    const handleLoad = () => {
        if (selectedConfig?.id == null) return;
        onLoad(selectedConfig);
    };

    const handleNew = () => {
        setSelectedConfig(null);
        onNew();
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="column" spacing={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Configuration
                </Typography>
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 1, alignItems: "center", mb: 1 }}>
                    <Autocomplete<ResolverConfigDto & { id: number }>
                        size="small"
                        sx={{ minWidth: 280 }}
                        data-testid="resolver-config-search"
                        value={selectedConfig?.id != null ? (selectedConfig as ResolverConfigDto & { id: number }) : null}
                        inputValue={searchQuery}
                        onInputChange={(_, value) => setSearchQuery(value)}
                        onOpen={() => {
                            if (!searchQuery.trim()) {
                                fetchConfigList("");
                            }
                        }}
                        onChange={(_, newValue) => setSelectedConfig(newValue)}
                        options={searchResults.filter((c): c is ResolverConfigDto & { id: number } => c.id != null)}
                        getOptionLabel={(opt) => `${opt.name} (${opt.id})`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        loading={isLoadingList}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search Configuration"
                                placeholder="Click to list configs, or type to search"
                                inputProps={{
                                    ...params.inputProps,
                                    "data-testid": "resolver-config-search-input",
                                } as typeof params.inputProps}
                            />
                        )}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleLoad}
                        disabled={selectedConfig?.id == null || isLoadingList}
                        data-testid="resolver-config-load"
                    >
                        Load
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleNew} data-testid="resolver-config-new">
                        New
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={onSave}
                        disabled={isSaving}
                        data-testid="resolver-config-save"
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </Button>
                    {rubricVersionId != null && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onSetActive}
                            disabled={loadedConfigId == null}
                            data-testid="resolver-config-set-active"
                        >
                            Set as active
                        </Button>
                    )}
                    {loadedConfigId != null && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={onDeleteClick}
                            data-testid="resolver-config-delete"
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
                {(loadError || saveError || saveSuccess || setActiveError) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                        {loadError && (
                            <Alert severity="error" onClose={onDismissLoadError} sx={{ py: 0.5 }}>
                                {loadError}
                            </Alert>
                        )}
                        {saveError && (
                            <Alert severity="error" onClose={onDismissSaveError} sx={{ py: 0.5 }}>
                                {saveError}
                            </Alert>
                        )}
                        {saveSuccess && (
                            <Alert severity="success" onClose={onDismissSaveSuccess} sx={{ py: 0.5 }}>
                                Saved.
                            </Alert>
                        )}
                        {setActiveError && (
                            <Alert severity="error" onClose={onDismissSetActiveError} sx={{ py: 0.5 }}>
                                {setActiveError}
                            </Alert>
                        )}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}
